import type {
    CarDataType,
    CompareMotorizationType,
    ModelGradeBodyTypeIdsType,
} from "../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import { getStorageItem, setStorageItem, WebStorageType } from "../../../common-deprecated/utils/storageUtils";
import type { DisplayOptionType, LocalStorageNewCarIdsType } from "./compareTypes";
import { NcCompareContext } from "../../../common-deprecated/types/NewCarCompareTypes";

// ----------------------------------------------------------------------
// Helper types and consts
// ----------------------------------------------------------------------
const LOCAL_STORAGE_IDS: Record<NcCompareContext, string> = {
    [NcCompareContext.Original]: "onlineRetail.compare.newCarData",
    [NcCompareContext.Leasing]: "onlineRetail.compare.leasingData",
};

type DisplayOptionTypeCollection = Record<DisplayOptionType["type"], boolean>;

type CompareLocalStorageParams = {
    country: string;
    language: string;
};

type CompareLocalStorageItemType = {
    carIds?: LocalStorageNewCarIdsType[];
    motorizationIds?: CompareMotorizationType[];
    colourIds?: string[];
    displayOptions?: DisplayOptionTypeCollection;
};

// ----------------------------------------------------------------------
// Storage fns
// ----------------------------------------------------------------------

/**
 * Set compare local storage, only overwriting the values supplied in the storageItem param.
 */

const setCompareLocalStorage = (
    params: CompareLocalStorageParams,
    storageItem: CompareLocalStorageItemType,
    storageId: string,
): void => {
    setStorageItem({ ...params, storageId }, storageItem, WebStorageType.local);
};

const getCompareLocalStorageData = (
    params: CompareLocalStorageParams,
    storageId: string,
): CompareLocalStorageItemType | null => {
    return getStorageItem<CompareLocalStorageItemType>({ ...params, storageId }, WebStorageType.local) || null;
};

// ----------------------------------------------------------------------
// Getters
// ----------------------------------------------------------------------
export const getCompareDisplayOptions = (
    params: CompareLocalStorageParams,
    context: NcCompareContext,
): DisplayOptionTypeCollection =>
    getCompareLocalStorageData(params, LOCAL_STORAGE_IDS[context])?.displayOptions ||
    ({} as DisplayOptionTypeCollection);

export const getCompareLocalStorage = (
    params: CompareLocalStorageParams,
    context: NcCompareContext,
): {
    modelGradeBodyTypeIds: ModelGradeBodyTypeIdsType[];
    motorizationIds: CompareMotorizationType[];
    colourIds: string[];
    displayOptions: DisplayOptionTypeCollection;
} => {
    const {
        carIds = [],
        motorizationIds = [],
        colourIds = [],
        displayOptions = {} as DisplayOptionTypeCollection,
    } = getCompareLocalStorageData(params, LOCAL_STORAGE_IDS[context]) || {};

    const mappedCarIds: ModelGradeBodyTypeIdsType[] = carIds.map((car) => ({
        model: car.modelId,
        submodelId: car.submodelId,
        gradeId: car.gradeId,
        bodyTypeId: car.bodyTypeId,
        configurationId: car.configurationId,
    }));

    return {
        modelGradeBodyTypeIds: mappedCarIds,
        motorizationIds,
        colourIds,
        displayOptions,
    };
};

// ----------------------------------------------------------------------
// Setters
// ----------------------------------------------------------------------

/**
 * Setting function for new cars compare localstorage
 * You have to set the properties as an empty array/object if you want to remove them
 * @param params {country: string; language: string;}
 * @param data Only pass the properties you want to set
 * @returns void
 */
export const setCompareNewCarsDataLocalStorage = (
    params: CompareLocalStorageParams,
    data: Partial<CompareLocalStorageItemType>,
    context: NcCompareContext,
): void =>
    setCompareLocalStorage(
        params,
        {
            ...(getCompareLocalStorageData(params, LOCAL_STORAGE_IDS[context]) || ({} as CompareLocalStorageItemType)),
            ...data,
        },
        LOCAL_STORAGE_IDS[context],
    );

// ----------------------------------------------------------------------
// Save utils
// ----------------------------------------------------------------------
export const gradesToCarIdsType = (grades: CarDataType[]): LocalStorageNewCarIdsType[] =>
    grades.map(({ modelId, gradeId, bodyTypeId, submodelId, configuration }) => {
        return { modelId, gradeId, bodyTypeId, submodelId, configurationId: configuration?.id };
    });

export const saveDisplayOptions = (
    selectedDisplayOption: DisplayOptionType,
    settings: CommonSettingsType,
    context: NcCompareContext,
): void => {
    const existingValue = getCompareDisplayOptions(settings, context);

    return setCompareNewCarsDataLocalStorage(
        settings,
        {
            displayOptions: {
                ...(existingValue || ({} as Record<DisplayOptionType["type"], boolean>)),
                [selectedDisplayOption.type]: selectedDisplayOption.onlyShowDifferences,
            },
        },
        context,
    );
};
